import { Component } from '@theme/component';

/**
 * DAVIDsTEA matcha discovery carousel.
 *
 * A centre-mode rail: the card nearest the middle of the viewport grows to 442x283 and gains
 * its two actions, while its neighbours sit at 399x189. The rail itself is a native
 * overflow-scroll, so a finger, a trackpad, a keyboard and a screen reader all work before this
 * file loads. What this adds is the notion of a centre card, the two arrows, and the floating
 * arrow over the right-hand card.
 *
 * The step is never computed. Because the centre card is wider than the others, the distance
 * from one card to the next changes depending on which one is active, so a fixed `scrollBy`
 * would drift out of alignment within a few clicks — the failure is slow enough to pass a quick
 * look and obvious after five. Moving means asking a specific card to centre itself, and the
 * browser works out the distance. That also survives a card size change in the stylesheet.
 *
 * **The active card is always an element, never an index.** Two indices exist here and they stop
 * agreeing the moment the rail rotates: the DOM order, which never changes, and the paint order,
 * which is the flex `order` the rotation rewrites. Reading the active card as a position in one
 * list and applying it to the other puts the zoom on an arbitrary card after the first arrow
 * press. Positions are still how the rail decides *which* card comes next, but the answer is
 * turned into an element straight away and everything downstream compares identity.
 *
 * While an arrow-driven move is in flight the target is held, and the scroll position stops
 * being consulted. It has to be: growing the incoming card from 399 to 442 shifts every card to
 * its right by 43px, so a `scrollLeft` worked out before the swap lands next to the card it
 * aimed at, and re-deriving the active card from that wrong position picks a different card
 * again on the next frame. Holding the target means the layout settles once, under a centre that
 * does not move. Dragging with a finger never sets a target, so it keeps reading the scroll
 * position, which is the whole point of a rail you can throw.
 *
 * **Under a finger the browser is what stops the rail, and the component is what opens the card.**
 * `scroll-snap-type` is switched on once the rail is at rest and off before anything is written to
 * it, so the two never hold the centre at the same time. That split is what lets a flick land on
 * the next card and a throw run down the rail without a line of velocity arithmetic here.
 *
 * The card opens in the frame the rail stops, around its own centre. Growing pushes it half its
 * gain off the axis the browser has just put it on, and that shift is paid back on `scrollLeft` in
 * the same breath, so the landing is one motion. Paid by a second glide, as it once was, the phone
 * showed the rail stopping, a pause, the card jumping sideways, and a short slide back.
 */

const ACTIVE = 'data-active';

/**
 * The card last selected in the theme editor, kept outside any one component on purpose.
 *
 * Changing a setting makes the editor render the section again, which throws the component away
 * and builds a new one. Held on the instance, the selection would go with it, and the new rail
 * would open on its first card after every nudge of a slider: the card being adjusted would slide
 * out of the middle, which is the one place its centre placement can be judged. Only ever written
 * in the editor, so on the storefront it stays null and nothing reads it.
 *
 * @type {{ sectionId: string, blockId: string } | null}
 */
let editorSelection = null;

/**
 * Whether a picture was shot on white rather than cut out: most of a ring just inside its edges is
 * opaque and close to white.
 *
 * Read on a 48px copy, which is plenty for a question about the edges and costs nothing to scan.
 * The ring sits 6% in from the border because a file can carry a rim of transparency, rounded
 * corners for instance, around a white field. Tried on every card file in the shop's library: the
 * one picture on white has 92% of its ring white, the cut-outs and the photographs 0%.
 *
 * A canvas that cannot be read, when the file comes from a host that refuses it, answers false and
 * the picture is left as it is.
 *
 * @param {HTMLImageElement} image
 * @returns {boolean}
 */
function isShotOnWhite(image) {
  const size = 48;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return false;

  context.drawImage(image, 0, 0, size, size);

  /** @type {Uint8ClampedArray} */
  let data;
  try {
    data = context.getImageData(0, 0, size, size).data;
  } catch {
    return false;
  }

  const near = Math.round(size * 0.06);
  const far = size - 1 - near;
  let white = 0;
  let total = 0;

  for (let i = near; i <= far; i++) {
    for (const [x, y] of [[i, near], [i, far], [near, i], [far, i]]) {
      const offset = (y * size + x) * 4;
      total++;
      if (data[offset + 3] > 240 && Math.min(data[offset], data[offset + 1], data[offset + 2]) > 225) white++;
    }
  }

  return white / total >= 0.5;
}

/**
 * @typedef {object} Refs
 * @property {HTMLElement} rail
 * @property {HTMLButtonElement} prev
 * @property {HTMLButtonElement} next
 * @property {HTMLButtonElement} [float]
 *
 * @extends {Component<Refs>}
 */
export class DtMatchaDiscovery extends Component {
  requiredRefs = ['rail', 'prev', 'next'];

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #frame;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settle;

  /** @type {boolean} */
  #placed = false;

  /** Whether the browser currently owns the rail's alignment. @type {boolean} */
  #snapped = false;

  /**
   * The card an arrow press is travelling to, held until the rail comes to rest. Null whenever
   * the visitor is the one moving the rail.
   *
   * @type {HTMLElement | null}
   */
  #target = null;

  /** How many times the settle has fired on a move that has not arrived yet. */
  #waits = 0;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #kick;

  /** Whether a finger is on the rail. Only ever consulted under a coarse pointer. */
  #touching = false;

  /** A settle that fell due while the finger was still down, owed to the lift. */
  #owed = false;

  connectedCallback() {
    super.connectedCallback();

    const { rail, prev, next, float } = this.refs;

    prev.addEventListener('click', this.#onPrev);
    next.addEventListener('click', this.#onNext);
    float?.addEventListener('click', this.#onNext);
    float?.addEventListener('mousedown', this.#onFloatPress);
    rail.addEventListener('scroll', this.#onScroll, { passive: true });
    rail.addEventListener('scrollend', this.#onScrollEnd);
    rail.addEventListener('touchstart', this.#onTouchStart, { passive: true });
    rail.addEventListener('touchend', this.#onTouchEnd, { passive: true });
    rail.addEventListener('touchcancel', this.#onTouchEnd, { passive: true });
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);

    this.#observer = new ResizeObserver(this.#onResize);
    this.#observer.observe(rail);

    this.#sync();

    // Tells the stylesheet the controls can be trusted. Arrows that cannot move anything are
    // worse than no arrows, so they stay out of the page until this point.
    this.setAttribute('data-ready', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    const { rail, prev, next, float } = this.refs;
    prev?.removeEventListener('click', this.#onPrev);
    next?.removeEventListener('click', this.#onNext);
    float?.removeEventListener('click', this.#onNext);
    float?.removeEventListener('mousedown', this.#onFloatPress);
    rail?.removeEventListener('scroll', this.#onScroll);
    rail?.removeEventListener('scrollend', this.#onScrollEnd);
    rail?.removeEventListener('touchstart', this.#onTouchStart);
    rail?.removeEventListener('touchend', this.#onTouchEnd);
    rail?.removeEventListener('touchcancel', this.#onTouchEnd);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    this.#observer?.disconnect();

    if (this.#frame) cancelAnimationFrame(this.#frame);
    if (this.#settle) clearTimeout(this.#settle);
    if (this.#kick) clearTimeout(this.#kick);
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.refs.rail.querySelectorAll('[data-discovery-item]')]);
  }

  get #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  /**
   * Whether the rail is being driven by a finger rather than by a pointer that hovers.
   *
   * It decides when the active card is repainted, and that turns out to matter a great deal.
   * Becoming active costs the card 29.3px of width at 390 — measured — and that width lands in
   * one frame, on purpose, because the centring arithmetic cannot read a number on its way
   * somewhere else. On a mouse that is invisible: the pointer is not holding the rail. Under a
   * finger it is the opposite, because everything to the right of the card jumps by 29.3px while
   * the finger is still on the glass.
   *
   * Read live rather than cached. A tablet with a keyboard folded back changes answer, and this
   * costs nothing next to what it guards.
   */
  get #coarse() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  /**
   * Hand the rail's alignment to the browser, or take it back.
   *
   * Under a finger this is what decides where a gesture ends. A short flick lands on the next
   * card and a long one runs down the rail, at the speed the phone's own compositor works out --
   * behaviour worth several hundred lines if it were written here, and better than any of them
   * because it is the one every other app on the device already has.
   *
   * It is off for every programmatic move, and that is the whole reason it can be on at all. The
   * component asks a card to centre itself by measuring where it is; snapping answers the same
   * question with its own nearest anchor, and since an active card is 43px wider than its
   * neighbours the two answers part company exactly when a card is growing. Rather than have them
   * argue, only one is ever awake: the browser while the visitor drives, the component the moment
   * anything is written to `scrollLeft`.
   *
   * Requested on a fine pointer it does nothing, and asks nothing of a mouse that was already
   * fine. Reading `#coarse` here rather than at connect also means a tablet that gains a mouse
   * gives the rail back on its next settle.
   *
   * @param {boolean} on
   */
  #snap(on) {
    const wanted = on && this.#coarse;
    if (wanted === this.#snapped) return;
    this.#snapped = wanted;
    this.refs.rail.toggleAttribute('data-snap', wanted);
  }

  /**
   * The card whose centre is closest to the centre of the rail. Distance rather than
   * containment: at the two ends of the rail no card sits on the axis, and a containment test
   * would leave the page with no active card at all just as the visitor reaches the last one.
   *
   * @returns {number}
   */
  get #activeIndex() {
    const items = this.#visualItems;
    if (!items.length) return -1;

    const rail = this.refs.rail.getBoundingClientRect();
    const axis = rail.left + rail.width / 2;

    let best = 0;
    let shortest = Infinity;

    items.forEach((item, index) => {
      const box = item.getBoundingClientRect();
      const distance = Math.abs(box.left + box.width / 2 - axis);
      if (distance < shortest) {
        shortest = distance;
        best = index;
      }
    });

    return best;
  }

  /**
   * How far the rail must travel to bring an item to its middle. Returned rather than applied,
   * so the same arithmetic serves the animated move and the silent initial placement.
   *
   * @param {HTMLElement} item
   * @returns {number}
   */
  #offsetFor(item) {
    const rail = this.refs.rail.getBoundingClientRect();
    const box = item.getBoundingClientRect();
    return this.refs.rail.scrollLeft + (box.left + box.width / 2) - (rail.left + rail.width / 2);
  }

  /**
   * How far the rail could still travel towards centring an item: `#offsetFor`, held inside the
   * range the rail can actually scroll.
   *
   * The hold is what makes it a measure of the gesture. At either end a card that is not yet
   * active cannot reach the middle — the padding is sized for the active width — so snapping
   * leaves it 14.7px short at 390 and the unclamped distance says the finger fell short. It did
   * not: that gap is closed by the card growing, and nothing is left to travel.
   *
   * @param {HTMLElement} item
   * @returns {number}
   */
  #reach(item) {
    const rail = this.refs.rail;
    const end = rail.scrollWidth - rail.clientWidth;
    return Math.min(Math.max(this.#offsetFor(item), 0), end) - rail.scrollLeft;
  }

  /**
   * Whether the rail is endless. Off unless the section asks for it, and that default is a
   * decision: rotating a card from one end of the rail to the other makes the browser repaint it,
   * and the repaint is visible as a slot that arrives empty and fills a beat later, on the card
   * coming in from the right. Bounded, nothing moves, so nothing is repainted, and the arrows
   * stop at each end instead.
   *
   * Three cards remains the floor even when it is asked for: under that, rotating would move the
   * card the visitor is looking at.
   */
  get #loops() {
    return this.hasAttribute('data-loop') && this.#items.length >= 3;
  }

  /** The gap between two cards, read from the stylesheet rather than repeated here. */
  get #gap() {
    return parseFloat(getComputedStyle(this.refs.rail).columnGap) || 0;
  }

  /**
   * The cards in the order they are painted, which is not the order they sit in the DOM.
   *
   * @returns {HTMLElement[]}
   */
  get #visualItems() {
    return this.#items.slice().sort((a, b) => Number(a.style.order || 0) - Number(b.style.order || 0));
  }

  /**
   * Keep at least one card on each side of the active one, so either arrow always has somewhere
   * to travel. This is what makes the rail endless whatever the merchant puts in it.
   *
   * The rotation is done with the flex `order` property, and nothing is ever moved in the DOM.
   * Moving an `<img>` in the DOM makes the browser drop and re-decode it, which shows as a card
   * arriving empty and filling a beat later. Reordering leaves every node exactly where it is,
   * so there is nothing to decode again and the swap is immediate.
   *
   * Cloning was never an option either: each card carries a real product form, so a copy would
   * put a second form for the same product, with the same element ids, in the page.
   *
   * Each rotation is paid back on `scrollLeft` in the same breath, so nothing shifts on screen.
   * Only ever called when the rail is at rest — during a smooth scroll the browser owns
   * `scrollLeft`, and writing to it mid-animation cancels the animation.
   */
  #balance() {
    if (!this.#loops) return;

    const rail = this.refs.rail;
    const gap = this.#gap;

    {
      // Bounded on the card count: each pass moves exactly one card, and needing more passes than
      // there are cards means the active index is not converging. Better a rail that stops
      // rotating than a loop that locks the tab.
      let guard = this.#items.length;

      while (guard-- > 0) {
        const items = this.#visualItems;
        const active = this.#activeIndex;

        if (active <= 0) {
          const last = /** @type {HTMLElement} */ (items[items.length - 1]);
          const width = last.getBoundingClientRect().width + gap;
          last.style.order = String(Number(items[0].style.order || 0) - 1);
          rail.scrollLeft += width;
        } else if (active >= items.length - 1) {
          const first = /** @type {HTMLElement} */ (items[0]);
          const width = first.getBoundingClientRect().width + gap;
          first.style.order = String(Number(items[items.length - 1].style.order || 0) + 1);
          rail.scrollLeft -= width;
        } else {
          break;
        }
      }
    }
  }

  /**
   * Give one card the active state and take it off every other. Identity, not position: the two
   * orders in play here disagree as soon as the rail rotates.
   *
   * @param {HTMLElement | null} active
   */
  #paint(active) {
    for (const item of this.#items) {
      if (item === active) item.setAttribute(ACTIVE, '');
      else item.removeAttribute(ACTIVE);
    }
  }

  /** @param {number} direction */
  #move(direction) {
    if (!this.#items.length) return;

    // An arrow names the card it wants. Snapping would answer with the nearest anchor to
    // wherever the smooth scroll happens to be passing, which is not the same card once one of
    // them is 43px wider than the rest.
    this.#snap(false);

    // Rotate first, then travel. Doing it in this order means the rotation always happens while
    // the rail is still, and there is always a card waiting in the direction asked for.
    this.#balance();

    const items = this.#visualItems;

    // Count from the card already being travelled to, not from the one nearest the middle right
    // now. Two quick presses would otherwise both start from the same place and only advance by
    // one, because the first move is still halfway through when the second is read.
    const from = this.#target ? items.indexOf(this.#target) : this.#activeIndex;
    if (from < 0) return;

    const wanted = from + direction;
    const target = this.#loops ? items[wanted] : items[Math.min(Math.max(wanted, 0), items.length - 1)];
    if (!target) return;

    this.#travel(target);
  }

  /**
   * Take the rail to one card and make it the active one. Shared by the arrows and by the theme
   * editor, which asks for a card by name rather than by direction.
   *
   * @param {HTMLElement} target
   */
  #travel(target) {
    this.#target = target;
    this.#waits = 0;

    // Resize before measuring. The incoming card gains 43px the moment it becomes active, which
    // pushes everything to its right along by the same amount, so an offset read before the swap
    // aims at where the card used to be.
    this.#paint(target);

    // Never `scrollIntoView`. Even with `block: 'nearest'` it is free to scroll every ancestor
    // that can scroll, and on this theme that includes `.page-wrapper`, which is the page. The
    // symptom was ugly and immediate: switching universe jumped the visitor down over the
    // flavour grid to land on this section. Writing `scrollLeft` touches this rail and nothing
    // else, by construction rather than by hoping an option holds.
    const departure = this.refs.rail.scrollLeft;
    const to = this.#offsetFor(target);
    this.refs.rail.scrollTo({ left: to, behavior: this.#behavior });

    // Check the result, not the call. A smooth programmatic scroll can be refused without saying
    // so: `scrollTo` returns, no `scroll` event ever follows, and the rail stays exactly where it
    // was. Measured on this rail: writing `scrollLeft` moved it 200px, and the identical move
    // asked for with `behavior: 'smooth'` moved it none, with `prefers-reduced-motion` off. Left
    // unchecked the component then waits on an animation that will never happen, which reads as
    // a carousel frozen one card behind its own zoom.
    //
    // So: if nothing has moved shortly after, put the value there. The visitor sees either a
    // glide or an instant step, and never a rail that ignores its arrows.
    if (this.#kick) clearTimeout(this.#kick);
    this.#kick = setTimeout(() => {
      this.#kick = undefined;
      if (Math.abs(to - departure) > 2 && this.refs.rail.scrollLeft === departure) {
        this.refs.rail.scrollLeft = to;
      }
    }, 120);

    // Arm the release by hand. When the rail is already where it was asked to go the browser
    // fires no scroll event at all, and the target would then be held for good, leaving the rail
    // deaf to the finger.
    this.#onSettled();
    this.#sync();
  }

  #onPrev = () => this.#move(-1);
  #onNext = () => this.#move(1);

  /**
   * Selecting a card in the theme editor brings it to the middle.
   *
   * That is where its picture settings are worth judging: the centre card is the taller one, the
   * one with the actions on it, and it can carry a placement of its own. Without this, adjusting a
   * card means pressing the arrows until it happens to arrive.
   *
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    const item = this.#editorItem(event);
    if (!item) return;

    const { sectionId = '', blockId = '' } = /** @type {CustomEvent} */ (event).detail ?? {};
    editorSelection = { sectionId, blockId };

    // Before the first placement the rail has no size, and `#place` will open on this card anyway.
    if (!this.#placed) return;

    this.#snap(false);
    this.#balance();
    this.#travel(item);
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (this.#editorItem(event)) editorSelection = null;
  };

  /**
   * The card an editor event is about, when it is one of this rail's.
   *
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #editorItem(event) {
    const item = event.target instanceof Element ? event.target.closest('[data-discovery-item]') : null;
    return item instanceof HTMLElement && this.refs.rail?.contains(item) ? item : null;
  }

  /**
   * The card the editor had selected before it rendered this section again, if it is in this rail.
   * Both universes number their cards `card_1`, `card_2`…, so the section has to match as well.
   *
   * @param {HTMLElement[]} items
   * @returns {HTMLElement | undefined}
   */
  #editorSelected(items) {
    if (!editorSelection) return undefined;

    const { sectionId, blockId } = editorSelection;
    if (!this.closest(`[id="shopify-section-${sectionId}"]`)) return undefined;

    return items.find((item) => {
      try {
        return JSON.parse(item.dataset.shopifyEditorBlock ?? '{}').id === blockId;
      } catch {
        return false;
      }
    });
  }

  /**
   * A width change moves the axis the rail centres on, and the card widths with it. Repainting is
   * not enough: the rail keeps the `scrollLeft` it had at the old width, so the card stays put
   * while the middle of the rail travels away from it. Measured at 11.5px off axis going from
   * 1296 to 390, which is the difference between a card that looks centred and one that looks
   * nudged.
   *
   * Routed through the same settle as everything else, so a drag being resized — a phone turning
   * in the hand — recentres once, at the end, rather than fighting the finger.
   */
  #onResize = () => {
    this.#sync();
    this.#onSettled();
  };

  /**
   * The floating arrow duplicates the next arrow, so it is `aria-hidden` and out of the tab
   * order. A mouse press would still hand it the DOM focus, and focus inside an `aria-hidden`
   * subtree is a real violation — the browser logs it and assistive technology loses track of
   * where it is. Cancelling the default on press stops the focus without touching the click,
   * which still fires.
   *
   * @param {MouseEvent} event
   */
  #onFloatPress = (event) => event.preventDefault();

  /**
   * Scroll fires far more often than the layout can usefully change, so the work is folded into
   * the next frame. Without this the active card is recomputed dozens of times per swipe, each
   * one reading `getBoundingClientRect` on every card.
   */
  #onScroll = () => {
    this.#onSettled();

    // Under a finger, nothing at all until the rail is still.
    //
    // `#sync` repaints the active card, and repainting mid-drag does two things a finger can
    // feel. It moves the layout — 29.3px of width to the card gaining the state, paid for by
    // everything to its right — and it starts a 460ms height transition on two cards at once,
    // which is an animation of layout rather than of composition. Both land on the main thread,
    // in the middle of the one gesture that must not stutter.
    //
    // Measured with eight cards at 390: writing the attributes and then reading layout back
    // costs 0.89ms a frame against 0.01ms for the same reads on their own, on a desktop. A phone
    // lays out several times slower.
    //
    // Nothing is lost by waiting. `#rest` repaints and recentres the moment the rail stops —
    // on `scrollend` where the browser has it, after 150ms of silence where it does not — which is
    // the whole behaviour: the rail glides under the finger, and the card it lands on opens once,
    // when the finger has gone.
    if (this.#coarse) return;

    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#sync();
    });
  };

  /**
   * Put the rail on its opening card, once, without animating.
   *
   * It cannot be done on connect. A section belonging to the other universe is inside a
   * `display: none` ancestor, so every box it owns measures zero and centring anything is a
   * no-op — the rail would stay on its first card for as long as that universe is the one not
   * showing, and land wrong the moment the visitor switches. The ResizeObserver already fires
   * when the section gains a size, which is exactly the moment this becomes possible.
   */
  #place() {
    const items = this.#items;
    if (this.#placed || !items.length || !this.refs.rail.clientWidth) return;

    // Nothing below is a gesture, so the browser does not get a say in where it lands.
    this.#snap(false);

    // Seed the paint order from the DOM order, once. Everything after this reads and writes
    // `order` only, so the DOM never changes shape again.
    items.forEach((item, index) => {
      item.style.order = String(index);
    });

    const start = Math.min(Number(this.dataset.initialIndex ?? 1), items.length - 1);
    const target = this.#editorSelected(items) ?? items[start];
    if (!target) return;

    // Grow it before measuring, for the same reason the arrows do: the offset that centres a
    // 399px card leaves a 442px one half its growth off the axis.
    this.#paint(target);

    this.refs.rail.scrollLeft = this.#offsetFor(target);
    this.#placed = true;

    // Thaw the rail's pictures now that the section is on screen.
    //
    // They ship `loading="lazy"`, and that is not decoration: a section belonging to the other
    // universe sits under `display: none`, never enters the viewport, and therefore downloads
    // nothing at all — measured at zero requests on the flavour grid, against 223 KiB once
    // shown. Marking them eager in the Liquid would throw that away and make both universes
    // fetch on every page load.
    //
    // On its own though, only the cards near the middle are ever fetched, so moving the rail
    // shows a white gap that fills a beat later. Lifting the attribute the moment the section
    // becomes measurable keeps the saving for the universe that stays hidden and loads the whole
    // rail for the one being looked at.
    for (const image of this.refs.rail.querySelectorAll('img[loading="lazy"]')) {
      image.setAttribute('loading', 'eager');
    }

    this.#probeBackgrounds();
  }

  /**
   * Blend away the white around the pictures whose block leaves that to the theme.
   *
   * Run here, with the rest of what waits for the section to be on screen, so the universe that
   * stays hidden asks for nothing. The check loads its own 64px copy, marked `crossOrigin`, and
   * never touches the picture on the card: in the theme editor the preview and the image host are
   * two different origins, and a canvas holding a picture drawn without CORS cannot be read. The
   * CDN answers `access-control-allow-origin: *`, so the copy can.
   */
  #probeBackgrounds() {
    for (const frame of this.refs.rail.querySelectorAll('[data-dt-card-probe]')) {
      const url = frame.getAttribute('data-dt-card-probe');
      if (!url || frame.hasAttribute('data-blend')) continue;

      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.addEventListener('load', () => {
        if (isShotOnWhite(probe)) frame.setAttribute('data-blend', '');
      });
      probe.src = url;
    }
  }

  #sync = () => {
    const { rail, prev, next, float } = this.refs;

    this.#place();

    const items = this.#visualItems;

    // Read the layout before writing any of it back.
    //
    // `#paint` sets and clears an attribute on every card, which invalidates their style; asking
    // for `scrollWidth` afterwards forces the browser to lay the rail out again there and then,
    // synchronously, before it can answer. Measured at 0.89ms a frame against 0.01ms for the
    // reads alone. Taking the two numbers first costs nothing and the reflow disappears: what is
    // written after them is not read again until the next frame.
    //
    // The overhang is a question about the rail and not about how many cards are in it: a rail
    // whose cards all fit has nowhere to travel, however many there are, and arrows over it are
    // two controls that do nothing. `scrollWidth` against `clientWidth` is the browser's own
    // answer, so it holds when the cards shrink on the width ramp and when the merchant changes
    // how many the band shows. One pixel of deadband, because both numbers are rounded and a
    // rail that fits exactly can report a one pixel overhang.
    const overhang = rail.scrollWidth - rail.clientWidth > 1;
    const activeIndex = this.#activeIndex;

    // An arrow press owns the centre until the rail stops. Re-deriving it from a scroll position
    // that is still moving — and moving through a layout the resize is changing underneath —
    // lands the zoom on a different card on every frame.
    if (!this.#target) this.#paint(items[activeIndex] ?? null);

    const active = this.#target ? items.indexOf(this.#target) : activeIndex;

    // An endless rail has no end to stop at, so neither arrow is ever disabled. A bounded one
    // switches each arrow off at its own end — read from the active card and not from the scroll
    // offset, which would switch the next arrow off while the last card is still only a
    // neighbour.
    //
    // The stylesheet fades a disabled arrow rather than taking it out of sight, so the pair is
    // either absent or complete and never shifts under the cursor, the rule on every carousel in
    // the theme. `disabled` and not `hidden`, so the button also leaves the tab order and no one
    // lands on a control that does nothing.
    //
    // The overhang is re-read on every scroll and every resize, which is what the two listeners
    // already give. It reads 0 while the section belongs to the universe that is not showing —
    // everything under `display: none` measures zero — and the ResizeObserver fires the moment
    // that changes, which is the same moment `#place` waits for.
    this.toggleAttribute('data-overflow', overhang);
    prev.disabled = this.#loops ? false : active <= 0;
    next.disabled = this.#loops ? false : active >= items.length - 1;

    // The floating arrow is the next arrow drawn a second time. It has to go when that one does,
    // or the rail ends with a control still offering a card that is not there.
    if (float) float.disabled = next.disabled;
  };

  /**
   * Rebalance once the rail has come to rest.
   *
   * A drag with a finger or a trackpad never goes through `#move`, so nothing would rotate and
   * the visitor would hit an end the arrows say does not exist. Debounced: the aim is a rail that
   * is demonstrably still, and 150ms of silence says that on every browser.
   *
   * Under a finger the debounce is only the fallback, see `#onScrollEnd`.
   */
  #onSettled = () => {
    if (this.#settle) clearTimeout(this.#settle);
    this.#settle = setTimeout(this.#rest, 150);
  };

  /**
   * Under a finger, settle the moment the browser says the rail has stopped.
   *
   * The 150ms of silence the debounce waits for is time the visitor watches: the rail lands on its
   * card, and the card sits there closed for a beat before it opens. `scrollend` fires once the
   * gesture is over, fling and snap included, one or two frames after the last scroll event on
   * this rail, so the card opens as the rail arrives.
   *
   * Only under a coarse pointer, so the arrows and the wheel keep the timing they were measured
   * with. Where `scrollend` does not exist (Safari only shipped it recently) nothing fires and the
   * debounce does the work, as it always has.
   */
  #onScrollEnd = () => {
    if (!this.#coarse) return;
    if (this.#settle) clearTimeout(this.#settle);
    this.#rest();
  };

  /**
   * A finger on the rail. Tracked so that nothing settles while it is still on the glass.
   *
   * Scroll events stop when the finger stops, not when it lifts, so a drag held still for 150ms
   * looked like a rail at rest to the debounce. Measured: a finger held 300ms mid-drag had the card
   * repainted and the rail glided 124px under it before it lifted.
   */
  #onTouchStart = () => {
    this.#touching = true;
  };

  /**
   * The last finger has lifted: pay any settle that fell due while it was down. Whatever the rail
   * does next, a fling or a snap, raises its own scroll events and pushes this back until it stops.
   *
   * @param {TouchEvent} event
   */
  #onTouchEnd = (event) => {
    if (event.touches.length) return;
    this.#touching = false;
    if (!this.#owed) return;
    this.#owed = false;
    this.#onSettled();
  };

  #rest = () => {
    this.#settle = undefined;

    if (this.#touching && this.#coarse) {
      this.#owed = true;
      return;
    }

    // A driven move is over when the rail has arrived, not when a timer says so. The browser can
    // take longer than this delay just to start a smooth scroll — measured at 155ms, against the
    // 150 here — and the safety net armed by `#move` then fires mid-flight: it releases the
    // target and repaints from a position the rail is still travelling through, putting the zoom
    // back on the card it had just left. Waiting again costs nothing, because a scroll that is
    // happening rearms this on its own.
    if (this.#target && this.#waits++ < 12) {
      const left = Math.abs(this.#offsetFor(this.#target) - this.refs.rail.scrollLeft);
      if (left > 2) {
        this.#onSettled();
        return;
      }
    }
    this.#waits = 0;

    // Whatever the rail was told to reach, it has reached. Release it before rebalancing, so
    // a finger laid on it in the next instant is heard.
    const arrived = this.#target;
    this.#target = null;

    // Everything from here rewrites the rail: a rotation, a card growing by 43px, and an offset
    // written straight to `scrollLeft`. Each one is a layout change, and each one would make
    // snapping re-align on an anchor of its choosing halfway through.
    this.#snap(false);

    this.#balance();

    // Rotating shuffles the paint order, so the card that was travelled to has to be named
    // again by identity. Deriving it from a position here is the same mistake by another road.
    const target = arrived ?? this.#visualItems[this.#activeIndex];
    if (!target) return;

    if (this.#coarse) {
      this.#land(target);
      return;
    }

    this.#paint(target);
    this.#sync();

    // Align the rail on its active card. Becoming active takes it from 399 to 442, and a flex item
    // grows to its right, so its centre walks 21.5px away from the axis it was just put on.
    // Closing that is this component's job, and it is the only alignment there is when the
    // pointer is a mouse.
    //
    // Written straight to `scrollLeft`, not animated. `#balance` pays its own rotation back in
    // the same breath, so anything left over is the sub-pixel residue of a smooth scroll, and
    // animating a fraction of a pixel would only raise another settle.
    //
    // The two-pixel deadband is what stops this from feeding itself: scrolling raises another
    // settle, which would align again, and browsers land on fractional offsets, so an exact
    // comparison would never be satisfied and the rail would creep for ever.
    const offset = this.#offsetFor(target);
    const drift = Math.abs(offset - this.refs.rail.scrollLeft);
    if (drift > 2) this.refs.rail.scrollLeft = offset;

    // Hand the alignment back here and not a line earlier. The rail is already on its card, so
    // switching snapping on is a no-op rather than a nudge -- turned on while the offset is
    // still wrong, it would pull the rail somewhere the visitor never asked for.
    this.#snap(true);
  };

  /**
   * Open the card a finger has brought to the middle, in one motion.
   *
   * Snapping stops the rail with the card on the axis, at the width it had a moment ago. Becoming
   * active adds 29.3px at 390, and a flex item grows to its right while the card that loses the
   * state gives its width back, so the centre walks 14.7px off the axis in one frame. That used to
   * be closed by a smooth `scrollTo` after the 150ms debounce, and on a phone it read as a lag: the
   * rail stops, a pause, the card jumps sideways, a short slide back. Three motions for one swipe.
   *
   * Now the shift is paid on `scrollLeft` in the same task as the repaint, before the browser
   * draws either, so the card grows around its own centre and its neighbours step aside evenly.
   * Nothing animates the width, which stays the rule on this rail: the width still lands in one
   * frame, only the place it lands from is held still. The height eases open as before.
   *
   * What the gesture itself left undone is measured before the repaint and kept apart. It is zero
   * when snapping did its job, which is every landing measured, and a glide carries it only when
   * it is real: a finger that let go while snapping was asleep, during one of the component's own
   * moves. Written in one step under `prefers-reduced-motion`, as before.
   *
   * @param {HTMLElement} target
   */
  #land(target) {
    const rail = this.refs.rail;

    // Read before the repaint, while the layout is still the one the finger left.
    const residue = this.#reach(target);

    this.#paint(target);

    const offset = this.#offsetFor(target);

    // Four pixels and not two: a glide of two pixels is a jump with extra steps, and it would
    // only raise another settle.
    const glide = Math.abs(residue) > 4 && this.#behavior === 'smooth';
    const landing = glide ? offset - residue : offset;

    // Same two-pixel deadband as the mouse path, for the same reason: this write raises a settle
    // of its own, and that one has to find nothing left to do.
    if (Math.abs(landing - rail.scrollLeft) > 2) rail.scrollLeft = landing;

    this.#sync();

    if (glide) {
      // Held while it travels, so the settle waits for the arrival rather than repainting from a
      // position the rail is still moving through, the same guard `#move` uses. Snapping stays
      // asleep, and the settle at the end of the glide hands it back.
      this.#target = target;
      this.#waits = 0;
      rail.scrollTo({ left: offset, behavior: 'smooth' });
      this.#onSettled();
      return;
    }

    // The rail is on its card, so this is a no-op rather than a nudge. See the mouse path.
    this.#snap(true);
  }
}

if (!customElements.get('dt-matcha-discovery-component')) {
  customElements.define('dt-matcha-discovery-component', DtMatchaDiscovery);
}
